import React, { useContext } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import IngredientCard from './IngredientCard/IngredientCard';
import FormContext from '../../Form/FormContext';
import { getIngredientEntriesByIds } from '../../../features/IngredientEntryContainer/selectors';
import { getLanguage } from 'features/app/selectors';
import { SortableContainer } from 'react-sortable-hoc';
import arrayMove from 'array-move';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'baseline'
  },
  ingredientCounts: {
    display: 'inline',
    fontWeight: 100
  },
  ingredientsCompletedText: {
    display: 'inline',
    marginRight: theme.spacing(1),
    textTransform: 'uppercase'
  },
  dragging: {
    pointerEvents: 'auto !important',
    cursor: 'grabbing !important'
  }
}));

function getPpiDetailDefaultData(itemCode) {
  return {
    itemCode,
    ppiEnrichmentDataId: null
  };
}

function recalculateStepNumbers(steps) {
  return steps.map((step, index) => {
    return { ...step, lineId: index + 1 };
  });
}

function findDuplicates(arr, key) {
  const seen = new Set();
  const duplicates = [];

  for (const item of arr) {
    const keyValue = item[key];
    if (seen.has(keyValue)) {
      duplicates.push(item);
    } else {
      seen.add(keyValue);
    }
  }

  return duplicates;
}

const IngredientsTabContent = () => {
  const classes = useStyles();
  const language = useSelector(getLanguage);
  const { data, setFieldValue, getFieldValue } = useContext(FormContext);
  const { ppiDetails = [], ingredientEntryIds, referencedIFoodData } = data;
  const getIngredientIds = getFieldValue('ingredientEntryIds') || [];
  const getppiDetails = getFieldValue('ppiDetails') || [];
  const ingredientEntries = useSelector(
    getIngredientEntriesByIds(getIngredientIds)
  ).toJS();

  const entryData =
    referencedIFoodData !== undefined
      ? _.sortBy(referencedIFoodData.referencedIngredientEntities, 'lineId')
      : null;
  const getItemCodez = entryData !== null ? entryData.map(i => i.itemCode) : [];

  const sortedIngredients = _.sortBy(ingredientEntries, 'lineId');

  const finalSortedIngredients = sortfunction(getItemCodez, sortedIngredients);

  const sortedIngredient =
    entryData !== null ? finalSortedIngredients : sortedIngredients;

  function getPpiDetailByItemCode(byItemCode) {
    const details = ppiDetails.filter(
      ppiDetail => ppiDetail.itemCode === byItemCode
    );
    let ppiDetail;
    if (details.length > 0) {
      [ppiDetail] = details;
    } else {
      ppiDetail = getPpiDetailDefaultData(byItemCode);
      ppiDetails.push(ppiDetail);
    }
    return ppiDetail;
  }

  function onChangeSelectedPpi(itemCode, event) {
    const ppiEnrichmentDataId = event.target.value;

    const ppiDetail = getPpiDetailByItemCode(itemCode);
    if (event.target.value === 'true' || event.target.value === 'false') {
      ppiDetail.ppiEnrichmentDataId = '';
      ppiDetail.ppiNotRequired = !ppiDetail.ppiNotRequired;
      setFieldValue('ppiDetails', ppiDetails);
    } else {
      ppiDetail.ppiEnrichmentDataId =
        ppiDetail.ppiEnrichmentDataId === ppiEnrichmentDataId
          ? ''
          : ppiEnrichmentDataId;
      ppiDetail.ppiNotRequired =
        ppiDetail.ppiEnrichmentDataId === ppiEnrichmentDataId ? false : false;
      setFieldValue('ppiDetails', ppiDetails);
    }
  }

  function isIngredientCompleted(itemCode) {
    const ppiDetail = getPpiDetailByItemCode(itemCode);
    return !!ppiDetail.ppiEnrichmentDataId || ppiDetail.ppiNotRequired;
  }

  function ingredientCount() {
    return sortedIngredient.length;
  }

  function ingredientsCompleted() {
    return sortedIngredient.reduce(
      (count, ingredientEntry) =>
        isIngredientCompleted(ingredientEntry.itemCode) ? count + 1 : count,
      0
    );
  }

  function sortfunction(ids, data) {
    const result = [];
    for (let i = 0; i < ids.length; i++) {
      const value = data.filter(j => j.itemCode === ids[i]);
      result.push(...value);
    }
    return result;
  }

  function sortEnd({ oldIndex, newIndex }) {
    if (sortedIngredient.length < 2) {
      return;
    }
    let rearrangedSteps = arrayMove(sortedIngredient, oldIndex, newIndex);

    rearrangedSteps = recalculateStepNumbers(rearrangedSteps);

    const sentData = rearrangedSteps.map(i => i.id);
    const getItemCode = rearrangedSteps.map(i => i.itemCode);
    const IngredientEntities = {
      ...data.referencedIFoodData,
      referencedIngredientEntities: rearrangedSteps
    };
    // const getPPIreordered = sortfunction(getItemCode, getppiDetails);
    const getPPIreordered = sortfunction(getItemCode, ppiDetails);

    setFieldValue('referencedIFoodData', IngredientEntities);
    setFieldValue('ppiDetails', getPPIreordered);
    setFieldValue('ingredientEntryIds', sentData);
  }

  let duplicatedPpis = [];
  if (data && data.referencedIFoodData) {
    duplicatedPpis = findDuplicates(
      data.referencedIFoodData.referencedIngredientEntities,
      'itemCode'
    );
  }

  return (
    <>
      <div className={classes.header}>
        <Typography
          variant="body1"
          classes={{ root: classes.ingredientsCompletedText }}
        >
          <FormattedMessage id="ppsi.ingredientsHandled" />
        </Typography>
        <Typography variant="h5" classes={{ root: classes.ingredientCounts }}>
          {ingredientsCompleted()}/{ingredientCount()}
        </Typography>
      </div>

      <SortableList
        onSortEnd={sortEnd}
        axis="y"
        distance={5}
        helperClass={classes.dragging}
        shouldCancelStart={e => {
          return duplicatedPpis.length;
        }}
      >
        <div>
          {sortedIngredient.map((ingredientEntry, index) => {
            const ppiSelection = getPpiDetailByItemCode(
              ingredientEntry.itemCode
            );
            return (
              <IngredientCard
                index={index}
                legacyFormulaCode={ingredientEntry.legacyFormulaCode}
                ingredientName={ingredientEntry.presentationName[language]}
                itemCode={ingredientEntry.itemCode}
                globalIngredientId={ingredientEntry.globalIngredientId}
                quantity={ingredientEntry.quantity}
                quantityUom={ingredientEntry.uomCode}
                owningOrganization={ingredientEntry.owningOrganization}
                ppis={ingredientEntry.ppis}
                isExcluded={ppiSelection.ppiNotRequired}
                selectedPpiDataId={ppiSelection.ppiEnrichmentDataId}
                completed={isIngredientCompleted(ingredientEntry.itemCode)}
                onChangeSelectedPpi={event => {
                  onChangeSelectedPpi(ingredientEntry.itemCode, event);
                }}
                key={ingredientEntry.id}
              />
            );
          })}
        </div>
      </SortableList>
    </>
  );
};

const SortableList = SortableContainer(({ children }) => {
  return children;
});

export default IngredientsTabContent;
